import { GraphPair } from '@models/meter-scans/ofdma/ofdma-graph-pair';
import { OdfmaMetaData } from '@models/meter-scans/ofdma/ofdma-meta-data';
import { OfdmaRaw } from '@models/meter-scans/ofdma/ofdma-raw';
import { UnitConversionUtils } from '@utilities/unit-conversion-utils';
import { ClassCast } from '@utilities/object-helper';

export class OfdmaScan {
    locked: boolean;
    @ClassCast(GraphPair)
    groupDelay: GraphPair[] = [];
    @ClassCast(OfdmaRaw)
    raw: OfdmaRaw;
    @ClassCast(GraphPair)
    impulse: GraphPair[] = [];
    @ClassCast(GraphPair)
    icfr: GraphPair[] = [];
    @ClassCast(GraphPair)
    transmit: GraphPair[] = [];
    @ClassCast(GraphPair)
    unwrappedPhase: GraphPair[] = [];
    @ClassCast(GraphPair)
    wrappedPhase: GraphPair[] = [];
    @ClassCast(GraphPair)
    icfrTilt: GraphPair[] = [];
    initialMetaData: OdfmaMetaData;

    private getValidGraphPoints(points: GraphPair[] | null | undefined): GraphPair[] {
        return Array.isArray(points)
            ? points.filter(point => point && Number.isFinite(point.x) && Number.isFinite(point.y))
            : [];
    }

    generateInitialMetaData(): void {
        if (!this.initialMetaData) {
            if (!this.raw || !Number.isFinite(this.raw.subcarrierZeroFrequency) ||
                !Number.isFinite(this.raw.subcarrierSpacing) ||
                !Number.isFinite(this.raw.firstActiveSubcarrierNumber) ||
                !Number.isFinite(this.raw.lastActiveSubcarrierNumber)) {
                return;
            }
            const subcarrierZeroFrequency = UnitConversionUtils.hzToMhz(this.raw.subcarrierZeroFrequency);
            const subcarrierSpacing = UnitConversionUtils.khzToMhz(this.raw.subcarrierSpacing);
            const freqStart = parseFloat((subcarrierZeroFrequency + this.raw.firstActiveSubcarrierNumber * subcarrierSpacing).toFixed(1));
            const freqEnd = parseFloat((subcarrierZeroFrequency + this.raw.lastActiveSubcarrierNumber * subcarrierSpacing).toFixed(1));

            this.initialMetaData = {
                subcarrierSpacing: this.raw.subcarrierSpacing + ' kHz',
                usChannelId: this.raw.preEq?.usChannelId ?? '',
                transmitPower: this.raw.transmitPower ,
                transmitPowerUnits:  'dBmV' ,
                freqStart: freqStart,
                freqStartUnits: 'MHz',
                freqEnd: freqEnd,
                freqEndUnits: 'MHz',
                freqWidth: parseFloat((freqEnd - freqStart).toFixed(1)),
                freqWidthUnits: 'MHz',
                centerFrequency: parseFloat((freqStart + ((freqEnd - freqStart)/2)).toFixed(1)),
              // TODO: need to get the actual value from the backend
              rx: 0,
              mer: 0,
              portMer: 0
            };
        }
    }

    get groupDelayWithMetaData() {
        this.generateInitialMetaData();
        const preEq = this.raw?.preEq;
        const formatMetric = (value: number | null | undefined, units: string): string =>
            typeof value === 'number' && Number.isFinite(value) ? `${parseFloat(value.toFixed(1))} ${units}` : '--';

        return {
            data: this.getValidGraphPoints(this.groupDelay),
            metaData: Object.assign({
                // unique
                ripplePeakToPeak: formatMetric(preEq?.GDRPP, 'nSec'),
                rippleRms: formatMetric(preEq?.GDRs, 'nSec'),
                slope: formatMetric(preEq?.GDS, 'nSec per MHz'),
                mean: formatMetric(preEq?.GDMean, 'nSec'),
            }, this.initialMetaData)
        };
    }

    get impulseWithMetaData() {
        this.generateInitialMetaData();
        const data = this.getValidGraphPoints(this.impulse);
        const dbEndValue = data.length && Number.isFinite(data[data.length - 1]?.y)
            ? `${parseFloat(data[data.length - 1].y.toFixed(1))}ft`
            : '--';
        const dbStartValue = data.length && Number.isFinite(data[0]?.y)
            ? `${parseFloat(data[0].y.toFixed(1))}ft`
            : '--';

        return {
            data,
            metaData: Object.assign({
                // unique
                dbAtStart: dbStartValue,
                dbAtEnd: dbEndValue
            }, this.initialMetaData)
        };
    }

    get icfrWithMetaData() {
        this.generateInitialMetaData();
        const preEq = this.raw?.preEq;
        const formatMetric = (value: number | null | undefined, units: string): string =>
            typeof value === 'number' && Number.isFinite(value) ? `${parseFloat(value.toFixed(1))} ${units}` : '--';

        return {
            data: this.getValidGraphPoints(this.icfr),
            icfrTiltData: this.getValidGraphPoints(this.icfrTilt),
            metaData: Object.assign({
                // unique
                ripplePeakToPeak: formatMetric(preEq?.ARPP, 'dB'),
                rippleRms: formatMetric(preEq?.ARRs, 'dB'),
                slope: formatMetric(preEq?.AS, 'dB per MHz'),
                mean: formatMetric(preEq?.AMean, 'dB')
            }, this.initialMetaData)
        };
    }

    get transmitWithMetaData() {
        this.generateInitialMetaData();

        return {
            data: this.getValidGraphPoints(this.transmit),
            metaData: Object.assign({
                // unique
            }, this.initialMetaData)
        };
    }

    get transmitWithIucData() {
        const iucProfileData: Record<string, string | number>[] = [];
        if (Array.isArray(this.raw?.iucProfile)) {
            this.raw.iucProfile.forEach(profile => {
                iucProfileData.push({
                    iuc: profile.iuc,
                    dataSymbolModulation: profile.dataSymbolModulation,
                    discontinuityTime: profile.discontinuityTime + ' sec',
                    outFramesCounter: profile.outFramesCounter
                });
            });
        }

        return {
            data: iucProfileData
        };
    }

    get wrappedPhaseWithMetaData() {
        this.generateInitialMetaData();

        return {
            data: this.getValidGraphPoints(this.wrappedPhase),
            metaData: this.initialMetaData
        };
    }

    get unwrappedPhaseWithMetaData() {
        this.generateInitialMetaData();

        return {
            data: this.getValidGraphPoints(this.unwrappedPhase),
            metaData: this.initialMetaData
        };
    }
}
